import * as React from "react";
import api, { $ } from "kw/api";
import { buildHistory } from "kw/util/buildHistory";
import { deref } from "kanopi-components/utils/primitives";
import { maybe } from "kanopi-components/utils/functional";
import { substituteProfileData } from "kw/util/substituteRiskData";
import { useAPI } from "kw/api/useAPI";
import {
  enquiryFields,
  questionSetFields,
  questionFields,
  riskProfileFields,
  claimProfileFields,
  quoteFields,
} from "kw/api/fragments";
import { useGlobal } from "kanopi-components/useGlobal";

type TUseStartQuestionFlowArgs = {
  questionSetKey?: string;
  mutationQuery: unknown;
  mutationQueryParams?: unknown;
  globalDataKey: string;
  questionFlowResponseKey: string;
  profileFieldName?: string;
  onQuestionFlowKnockout: (profile: Record<string, string>) => void;
};

export function useStartQuestionFlow({
  questionSetKey,
  mutationQuery,
  globalDataKey,
  questionFlowResponseKey,
  profileFieldName = "riskProfile",
  mutationQueryParams,
  onQuestionFlowKnockout,
}: TUseStartQuestionFlowArgs) {
  const [, setData] = useGlobal(globalDataKey);
  const [localData, setLocalData] = React.useState({});
  const { mutation, loading } = useAPI();
  const [answerSubmitting, setAnswerSubmitting] = React.useState<boolean>();

  const handleResponse = (response, responseKey) => {
    const [enquiry, profile] = substituteProfileData(
      deref(response, `${responseKey}.currentEnquiry`, {}),
      deref(response, `${responseKey}.${profileFieldName}`, {})
    );
    buildHistory(profile, deref(response, `${responseKey}.history`));
    enquiry.currentSet = deref(response, `${responseKey}.currentSet`, 0);
    enquiry.maxSets = deref(response, `${responseKey}.maxSets`, 0);
    enquiry.emptyQuestionFlow =
      enquiry.maxSets === 0 &&
      enquiry.currentSet === enquiry.maxSets &&
      enquiry?.questionSet === undefined;
    setLocalData({
      enquiry,
      complete: !deref(response, `${responseKey}.currentEnquiry`),
      profile,
      restart: false,
      enabledProducts: undefined, // Disable for subsequent
      history: profile?.history,
      questionSet: enquiry?.questionSet,
      profileData: profile?.data || {},
      currentSet: enquiry?.currentSet,
      maxSets: enquiry?.maxSets,
    });
    localData[profileFieldName] = { ...profile };
    setData(localData);
    maybe(onQuestionFlowKnockout)(profile);
  };

  async function startQuestionFlow(query, onResponse?: (res) => void) {
    mutation(
      query || mutationQuery,
      (response) => {
        handleResponse(response, questionFlowResponseKey);
        onResponse?.(response);
      },
      mutationQueryParams
    );
  }

  const answerEnquiry = async (
    questionFlowId: string,
    answers: object,
    onAnswerSubmissionSuccess: (_currentSet: number, _maxSets: number) => void,
    skipNext = false
  ) => {
    setAnswerSubmitting(true);
    try {
      /* Do not use useAPI here as any errors thrown while invoking API will be propagated
      to useAPI.  As the form validation is dependent on backend
      errors, the api error will need to be propagated to the caller */
      const response = await api.mutation(getAnswerEnquiryQuery(), {
        input: {
          questionFlowId,
          answers: answers ? JSON.stringify(answers) : answers,
          skipNext,
        },
      });
      const _currentSet = deref(response, "answerEnquiry.currentSet", 0);
      const _maxSets = deref(response, "answerEnquiry.maxSets", 0);
      handleResponse(response, "answerEnquiry");
      onAnswerSubmissionSuccess(_currentSet, _maxSets);
    } finally {
      setAnswerSubmitting(false);
    }
  };
  React.useEffect(() => {
    if (shouldCallQuery(localData, questionSetKey)) {
      startQuestionFlow();
    }
  }, [questionSetKey]);
  return {
    enquiry: localData?.enquiry,
    questionSet: localData?.enquiry?.questionSet,
    profileData: localData?.profile?.data || {},
    currentSet: localData?.enquiry?.currentSet,
    maxSets: localData?.enquiry?.maxSets,
    answerEnquiry,
    refresh: startQuestionFlow,
    loading,
    saving: answerSubmitting,
    history: localData?.history,
    startQuestionFlow,
  };
}

function shouldCallQuery(data, questionSetKey) {
  return (
    deref(data, "enquiry.questionSet.key") != questionSetKey ||
    questionSetKey === undefined
  );
}

export const getAnswerEnquiryQuery = () => {
  return {
    answerEnquiry: [
      {
        input: $("input", "AnswerEnquiryInput!"),
      },
      {
        currentEnquiry: {
          ...enquiryFields,
          questionSet: {
            ...questionSetFields,
            questions: {
              ...questionFields,
            },
          },
        },
        "... on QuoteQuestionFlowType": {
          riskProfile: riskProfileFieldsWithQuote,
        },
        "... on CoverQuestionFlowType": {
          riskProfile: riskProfileFieldsWithQuote,
        },
        "... on CoverSummaryQuestionFlowType": {
          riskProfile: riskProfileFieldsWithQuote,
        },
        "... on QuoteSummaryQuestionFlowType": {
          riskProfile: riskProfileFieldsWithQuote,
        },
        "... on KnockoutQuestionFlowType": {
          riskProfile: riskProfileFieldsWithQuote,
        },
        "... on LegalQuestionFlowType": {
          riskProfile: riskProfileFieldsWithQuote,
        },
        "... on PolicyDetailsQuestionFlowType": {
          riskProfile: riskProfileFieldsWithQuote,
        },
        "... on PolicyHolderQuestionFlowType": {
          riskProfile: riskProfileFieldsWithQuote,
        },
        "... on ClaimQuestionFlowType": {
          claimProfile: claimProfileFields,
        },
        "... on AdjustmentQuestionFlowType": {
          riskProfile: claimProfileFields,
        },
        "... on ExposureQuestionFlowType": {
          riskProfile: riskProfileFieldsWithQuote,
        },
        history: {
          questionSet: {
            id: true,
            key: true,
            label: true,
          },
        },
        maxSets: true,
        currentSet: true,
        messages: {
          kind: true,
          content: true,
        },
      },
    ],
  };
};

const riskProfileFieldsWithQuote = {
  ...riskProfileFields,
  quotes: {
    ...quoteFields,
  },
};
